import * as React from "react";
import { observer } from "mobx-react";

import { UitkLink } from "@egds/react-core/link";
import { AnalyticsConfig } from "@egds/react-core/utils";
import { UitkCard, UitkCardContentSection } from "@egds/react-core/cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";
import { UitkIcon, UitkIconSize } from "@egds/react-core/icons";
import { UitkText } from "@egds/react-core/text";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { withStores } from "stores";
import { UitkSpacing } from "@egds/react-core/spacing";
import JumplinksTitle from "./components/JumplinksTitle";
import TravelGuideLink from "./components/TravelGuideLink";
import { getJumplinkLocalizedNames, getJumplinkTitleString } from "./JumplinksUtils";

import { JumplinksProps, JumplinkItem, JumplinksTemplateComponentConfig } from "./typings";

const Jumplinks = withStores(
  "analytics",
  "context",
  "dx",
  "flexModuleModelStore"
)(
  observer((props: JumplinksProps) => {
    const { templateComponent, flexModuleModelStore, context, dx } = props;
    const {
      metadata: { id },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as JumplinksTemplateComponentConfig;
    const { jumplinksArray } = dx;

    const keyHelper = new ItemKeyHelper("jumplinks");
    const [isVisible, setIsVisible] = React.useState(false);
    const toggle = React.useCallback(() => setIsVisible(!isVisible), [isVisible]);
    const expandoKeyboardPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        toggle();
      }
    };

    // Main logic in the following is covered in test, only branch coverage is lacking
    /* istanbul ignore next */
    React.useEffect(() => {
      const scrollOnPageLoad = (): void => {
        const jumplinkPathName = location.hash.replace("#", "");

        const associatedJumplink = jumplinksArray.find(
          (jumplink: JumplinkItem) => jumplink.pathName === jumplinkPathName
        );

        if (!associatedJumplink) {
          return;
        }

        const element = document.getElementById(associatedJumplink.id);
        if (!element) {
          return;
        }

        scrollTo(element);
      };

      window.addEventListener("load", scrollOnPageLoad);

      return () => window.removeEventListener("load", scrollOnPageLoad);
    });

    const handleClick = (jumpLinkId: string) => (): void => {
      const element = document.getElementById(jumpLinkId);
      if (!element) {
        return;
      }

      scrollTo(element);
    };

    /* istanbul ignore next */
    const scrollTo = (elementToScrollTo: HTMLElement): void => {
      let wizardHeight = 0;

      const { deviceInformation } = context;
      const isMobile = deviceInformation?.mobile;

      if (!isMobile) {
        const wizardElement = document.getElementById("wizard-hotel-pwa-1");

        if (wizardElement) {
          wizardHeight = wizardElement.offsetHeight;
        }
      }

      const offsetPosition = elementToScrollTo.offsetTop;

      getFirstNavElement(elementToScrollTo).focus({ preventScroll: true });

      if (offsetPosition) {
        window.scrollTo({
          top: offsetPosition - wizardHeight,
          behavior: "smooth",
        });
      } else {
        elementToScrollTo.scrollIntoView();
      }
    };

    /* istanbul ignore next */
    const getFirstNavElement = (parentElement: HTMLElement): HTMLElement => {
      if (parentElement.getElementsByTagName("a").length) {
        return parentElement.getElementsByTagName("a")[0];
      }

      if (parentElement.getElementsByTagName("button").length) {
        return parentElement.getElementsByTagName("button")[0];
      }

      return parentElement;
    };

    const getAnalytics = (jumplinkName: string): AnalyticsConfig => ({
      id: `TG.LP.Dest.JumpTo.jumplinks.${jumplinkName}`,
      callback: (moduleId: string, description: string) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: moduleId,
          action: Action.CLICK,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
      },
    });

    const jumplLinkItems = (jumplinkItemsArray: JumplinkItem[]) =>
      jumplinkItemsArray.map((jumplink: JumplinkItem) => (
        <UitkLink
          key={keyHelper.next()}
          onClick={handleClick(jumplink.id)}
          analytics={getAnalytics(jumplink.pathName)}
          aria-label={getJumplinkLocalizedNames(jumplink)}
        >
          <a tabIndex={0} href={`#${jumplink.pathName}`}>
            {getJumplinkLocalizedNames(jumplink)}
          </a>
        </UitkLink>
      ));

    const cardView = (isMobile: boolean) => (
      <UitkCard>
        <UitkCardContentSection>
          {!isMobile && <JumplinksTitle model={model} context={context} />}
          {jumplLinkItems(jumplinksArray)}
        </UitkCardContentSection>
        <TravelGuideLink model={model} context={context} />
      </UitkCard>
    );

    const expandoView = (isMobile: boolean) => (
      <UitkSpacing margin={{ blockstart: "two" }}>
        <UitkCard>
          <div
            className="CallToAction"
            tabIndex={0}
            role="button"
            aria-expanded={isVisible}
            onClick={toggle}
            data-testid="expando-toggle"
            onKeyDown={expandoKeyboardPress}
          >
            <UitkCardContentSection padded>
              <UitkLayoutFlex>
                <UitkLayoutFlexItem grow={1}>
                  <UitkText size={isMobile ? 400 : 500} weight="bold" theme="default">
                    {getJumplinkTitleString(model, context)}
                  </UitkText>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem alignSelf="center" grow={0}>
                  <UitkIcon name={isVisible ? "expand_less" : "expand_more"} size={UitkIconSize.SMALL} />
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkCardContentSection>
          </div>
          <UitkCardContentSection
            padded={["blockend", "inlinestart", "inlineend"]}
            className={`${isVisible ? "" : "is-hidden"}`}
          >
            <UitkSpacing padding={{ blockend: "one", inline: "one" }}>
              <div>{cardView(isMobile)}</div>
            </UitkSpacing>
          </UitkCardContentSection>
        </UitkCard>
      </UitkSpacing>
    );

    return (
      <div id={id} className="Jumplinks" data-testid="jumplinks">
        <Viewport>
          <ViewMedium>{cardView(false)}</ViewMedium>
          <ViewSmall>{expandoView(true)}</ViewSmall>
        </Viewport>
      </div>
    );
  })
);

export default Jumplinks;
